import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { TagChips } from '../../components/common/EventTags';
import { apiErrorText, apiRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyFeed.css';
import './Clubs.css';

/* The company admin's clubs: every club with what is going on in it, and founding a new one.

   Only the company's admins found a club; who RUNS it is chosen on the club's own page (Members
   ▸ Make admin), where the colleague search already is. One row per club, and a row opens the
   club. The numbers are counts the server groups in a fixed handful of queries; the score is the
   members' own engagement score, the one the company already has. */

const ClubsManage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tags, setTags] = useState([]);
  const [draft, setDraft] = useState(null);     // {name, description, tags}
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/clubs/overview').then(setData).catch(() => navigate('/clubs'));
    apiRequest('GET', '/clubs').then((r) => setTags(r.tags || [])).catch(() => setTags([]));
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const create = () => {
    if (busy || !draft || !draft.name.trim()) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/clubs', draft)
      .then((res) => navigate(`/clubs/${res.club.id}`))
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const rows = (data && data.clubs) || [];
  const totals = (data && data.totals) || {};

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="users-gear" theme="primary" className="me-2" />
          {t('clubsManage')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('clubsManageNote')}</p>
        {data && totals.clubs > 0 && (
          <p className="ce-note">{t('clubPeopleInAClub', { n: totals.people_in_a_club || 0 })}</p>
        )}
        <p className="ce-note">{t('clubScoreHint')}</p>

        {!draft && (
          <button type="button" className="bravos-submit"
                  onClick={() => setDraft({ name: '', description: '', tags: [] })}>
            <Icon name="plus" /> {t('clubNew')}
          </button>
        )}
        {draft && (
          <div className="club-form mt-2">
            <label htmlFor="new-club-name">{t('clubName')}</label>
            <input id="new-club-name" className="cf-input" maxLength={50} value={draft.name}
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <label htmlFor="new-club-desc">{t('clubDescription')}</label>
            <textarea id="new-club-desc" className="cf-input" rows={3} maxLength={2000}
                      value={draft.description}
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            {tags.length > 0 && (
              <>
                <label>{t('clubTags')}</label>
                <TagChips tags={tags} selected={draft.tags}
                          onToggle={(id) => setDraft({ ...draft,
                            tags: draft.tags.includes(id)
                              ? draft.tags.filter((x) => x !== id) : [...draft.tags, id] })} />
              </>
            )}
            <div className="club-actions">
              <button type="button" className="bravos-submit" onClick={create}
                      disabled={busy || !draft.name.trim()}>{t('clubCreate')}</button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setDraft(null)}>{t('cancel')}</button>
            </div>
          </div>
        )}
        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      <Card>
        {data === null ? <span className="auth__spinner" /> : rows.length === 0 ? (
          <p className="ce-note">{t('clubsEmptyAdmin')}</p>
        ) : (
          <div className="club-table-wrap">
            <table className="club-table">
              <thead>
                <tr>
                  <th>{t('clubsTitle')}</th>
                  <th>{t('clubColPeople')}</th>
                  <th>{t('clubColWaiting')}</th>
                  <th>{t('clubColUpcoming')}</th>
                  <th>{t('clubColHeld')}</th>
                  <th>{t('clubColGoing')}</th>
                  <th>{t('clubColPosts')}</th>
                  <th>{t('clubColPhotos')}</th>
                  <th>{t('clubColActive')}</th>
                  <th title={t('clubScoreHint')}>{t('clubColScore')} ⓘ</th>
                  <th>{t('clubColLast')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/clubs/${c.id}`)}>
                    <td>
                      <strong>{c.name}</strong>
                      {c.is_archived && <span className="club-status club-status--archived ms-2">{t('clubArchived')}</span>}
                      {c.admins === 0 && !c.is_archived && (
                        <span className="club-status club-status--requested ms-2">{t('clubNoAdmin')}</span>
                      )}
                    </td>
                    <td>{c.member_count}</td>
                    <td>{c.pending_count || ''}</td>
                    <td>{c.upcoming_count}</td>
                    <td>{c.events_held}</td>
                    <td>{c.going_count}</td>
                    <td>{c.post_count}</td>
                    <td>{c.photo_count}</td>
                    <td>{c.club_score === null ? '' : c.active_count}</td>
                    <td>{c.club_score === null ? '–' : c.club_score}</td>
                    <td>{c.last_activity ? shortDate(c.last_activity, i18n.language) : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Base>
  );
};

export default ClubsManage;
